import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { logger } from "../../../lib/logger";
import { useBiz, errText } from "./i18n";
import { Sheet, Field, Card, Empty, inp, btnMain, btnSoft } from "./ui";

const newKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const SaleForm = ({ stock, t, onClose, onSaved }) => {
  const [f, setF] = useState({ product_id: stock[0]?.id || "", qty: 1, price: stock[0]?.price ?? "" });
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/simple/sales", { ...f, client_key: key.current });
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };
  return (
    <Sheet title={t("record_sale")} onClose={onClose} testid="sale-form">
      <form onSubmit={save} className="grid grid-cols-2 gap-3">
        <Field label={t("perfume")} className="col-span-2">
          <select value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value, price: stock.find((s) => s.id === e.target.value)?.price ?? "" })} data-testid="sale-perfume" className={inp}>
            {stock.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.remaining})</option>)}
          </select>
        </Field>
        <Field label={t("qty_sold")}><input required type="number" min="1" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="sale-qty" className={inp} /></Field>
        <Field label={t("price")}><input required type="number" step="any" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="sale-price" className={inp} /></Field>
        <button disabled={busy} data-testid="sale-save" className={`${btnMain} col-span-2 mt-2`}>{t("save")}</button>
      </form>
    </Sheet>
  );
};

const SaleLine = ({ s, t, aed, onUndo }) => (
  <div className={`flex items-center gap-3 px-4 py-3 ${s.status !== "active" ? "opacity-50" : ""}`} data-testid={`sale-${s.id}`}>
    <div className="flex-1 min-w-0">
      <p className="text-[15px]">{s.product} × {s.qty}</p>
      <p className="text-xs text-[#8A8173]">
        {t(s.source)}{s.ref ? ` · ${s.ref}` : ""}{s.batches.length ? ` · ${s.batches.join(", ")}` : ""} · {new Date(s.at).toLocaleDateString()}
        {s.status !== "active" && ` · ${t("refunded")}`}
      </p>
    </div>
    <span className="font-semibold whitespace-nowrap" dir="ltr">{aed(s.revenue)}</span>
    {s.source === "manual" && s.status === "active" && <button onClick={() => onUndo(s)} data-testid={`sale-undo-${s.id}`} className={btnSoft}><Undo2 size={14} />{t("undo")}</button>}
  </div>
);

export function SalesStock() {
  const { t, aed } = useBiz();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/admin/simple/sales").then((r) => setD(r.data)).catch((e) => logger.error("sales", e)), []);
  useEffect(() => { load(); }, [load]);
  const undo = async (s) => {
    if (!window.confirm(t("confirm_undo"))) return;
    try {
      await api.delete(`/admin/simple/sales/${encodeURIComponent(s.id)}`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };
  if (!d) return <p className="text-[#8A8173]">…</p>;
  return (
    <div data-testid="biz-sales">
      <p className="text-[13px] text-[#6B645A] mb-2">{t("stock_title")}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {d.stock.map((s) => (
          <Card key={s.id} testid={`stock-${s.id}`}>
            <p className="font-semibold">{s.name}</p>
            <div className="flex gap-6 mt-2">
              <div><p className="text-xs text-[#8A8173]">{t("remaining")}</p><p className="text-3xl font-semibold" data-testid={`stock-remaining-${s.id}`}>{s.remaining}</p></div>
              <div><p className="text-xs text-[#8A8173]">{t("sold")}</p><p className="text-3xl font-semibold text-[#2F6B3A]" data-testid={`stock-sold-${s.id}`}>{s.sold}</p></div>
            </div>
          </Card>
        ))}
      </div>
      <div className="flex items-center justify-between mt-7 mb-2">
        <p className="text-[13px] text-[#6B645A]">{t("recent_sales")}</p>
        <button onClick={() => setOpen(true)} disabled={!d.stock.length} data-testid="sale-new" className={btnMain}><Plus size={16} />{t("record_sale")}</button>
      </div>
      {d.sales.length === 0 ? <Empty>{t("no_sales")}</Empty> : (
        <Card className="!p-0 divide-y divide-[#F1EBE1]" testid="sales-list">{d.sales.map((s) => <SaleLine key={s.id} s={s} t={t} aed={aed} onUndo={undo} />)}</Card>
      )}
      {open && <SaleForm stock={d.stock} t={t} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
